// src/components/Home/ProjectsSection.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Project } from '../../types';
import ProjectCard from '../UI/ProjectCard';
import LoadingSpinner from '../UI/LoadingSpinner';
import { apiService } from '../../services/api';
import { featuredProjects } from '../../data/projectsData'; // Import from shared data

const ProjectsSection: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiService.getProjects();
        setProjects(response.data.slice(0, 6)); // Show only 6 projects on home page
      } catch (error) {
        console.error('Error fetching projects:', error);
        // If API fails, use only featured projects
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const categories = [
    { 
      key: 'all', 
      label: 'All Projects', 
      icon: '📋',
      description: 'Explore all our impactful initiatives',
      color: 'from-gray-500 to-gray-700'
    },
    { 
      key: 'food_security', 
      label: 'Food Security', 
      icon: '🌾',
      description: 'Sustainable agriculture and nutrition programs',
      color: 'from-green-500 to-emerald-600',
      content: 'Every African deserves dignified access to safe, nutritious food. We work with communities to promote productive food systems that respect our land, traditions, and indigenous knowledge.'
    },
    { 
      key: 'climate_action', 
      label: 'Climate Action', 
      icon: '🌍',
      description: 'Climate-resilient practices and environmental protection',
      color: 'from-blue-500 to-cyan-600',
      content: 'Empowering communities with climate-smart solutions rooted in indigenous knowledge and modern innovation to safeguard natural resources and strengthen food production.'
    },
    { 
      key: 'youth_empowerment', 
      label: 'Youth Empowerment', 
      icon: '👨‍🎓',
      description: 'Training future leaders and changemakers',
      color: 'from-orange-500 to-amber-600',
      content: 'Equipping youth with practical skills and innovative tools to transform agriculture, build climate resilience, and drive sustainable development.'
    },
    { 
      key: 'women_empowerment', 
      label: 'Women Empowerment', 
      icon: '👩‍🌾',
      description: 'Supporting female agripreneurs and leaders',
      color: 'from-purple-500 to-pink-600',
      content: 'Honoring women as guardians of food security by dismantling barriers and advocating for their full participation in leadership and agri-business.'
    }
  ];

  // Use the imported featuredProjects instead of redefining
  const allProjects = [...featuredProjects, ...projects];
  
  const filteredProjects = activeCategory === 'all' 
    ? allProjects.slice(0, 6)
    : allProjects.filter(project => project.category === activeCategory).slice(0, 6);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const categoryVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.05,
      transition: { duration: 0.2 }
    },
    active: {
      scale: 1.1,
      transition: { duration: 0.2 }
    }
  };

  // Gradient styles for category buttons
  const getGradientStyle = (colorClass: string) => {
    const gradients: { [key: string]: string } = {
      'from-green-500 to-emerald-600': 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      'from-blue-500 to-cyan-600': 'linear-gradient(135deg, #3b82f6 0%, #0891b2 100%)',
      'from-orange-500 to-amber-600': 'linear-gradient(135deg, #f97316 0%, #d97706 100%)',
      'from-purple-500 to-pink-600': 'linear-gradient(135deg, #8b5cf6 0%, #db2777 100%)',
      'from-gray-500 to-gray-700': 'linear-gradient(135deg, #6b7280 0%, #374151 100%)'
    };
    return gradients[colorClass] || gradients['from-gray-500 to-gray-700'];
  };

  return (
    <section className="py-5 bg-white position-relative overflow-hidden">
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute top-0 start-0 w-50 h-100 bg-gradient-to-r from-green-50 to-transparent opacity-50"></div>
        <motion.div
          className="position-absolute"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            top: '10%',
            right: '5%',
            fontSize: '4rem',
            opacity: 0.1
          }}
        >
          🌱
        </motion.div>
      </div>

      <div className="container position-relative z-2">
        {/* Header Section */}
        <motion.div 
          className="row text-center mb-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="col-12">
            <motion.div
              variants={itemVariants}
              className="d-inline-flex align-items-center px-3 py-2 rounded-pill bg-success bg-opacity-10 mb-3"
            >
              <span className="text-success fw-semibold small">🌟 Featured Initiatives</span>
            </motion.div>
            
            <motion.h2 
              variants={itemVariants}
              className="display-4 fw-black text-dark mb-3"
            >
              Transforming Communities
              <span className="d-block text-success">Through Action</span>
            </motion.h2>
            
            <motion.p 
              variants={itemVariants}
              className="lead text-muted fs-5 mx-auto"
              style={{ maxWidth: '600px', lineHeight: '1.6' }}
            >
              Discover our impactful initiatives that are creating sustainable change 
              and empowering communities across Africa.
            </motion.p>
          </div>
        </motion.div>

        {/* Interactive Category Filter */}
        <motion.div 
          className="row mb-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="col-12">
            <div className="text-center mb-4">
              <h4 className="fw-semibold text-dark mb-3">Explore Our Focus Areas</h4>
            </div>
            
            <div className="d-flex flex-wrap justify-content-center gap-3">
              {categories.map((category) => (
                <motion.button
                  key={category.key}
                  className={`position-relative overflow-hidden border-0 rounded-4 p-4 text-white fw-semibold ${
                    activeCategory === category.key ? 'shadow-lg' : 'shadow'
                  }`}
                  style={{
                    background: getGradientStyle(category.color),
                    minWidth: '160px',
                    minHeight: '120px'
                  }}
                  onClick={() => setActiveCategory(category.key)}
                  onMouseEnter={() => setHoveredCategory(category.key)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  variants={categoryVariants}
                  whileHover="hover"
                  animate={activeCategory === category.key ? "active" : "initial"}
                >
                  <div className="position-relative z-2">
                    <div className="fs-2 mb-2">{category.icon}</div>
                    <div className="small">{category.label}</div>
                  </div>
                  
                  {/* Hover Overlay */}
                  <motion.div
                    className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-20"
                    initial={{ x: '-100%' }}
                    animate={{ x: hoveredCategory === category.key ? '0%' : '-100%' }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              ))}
            </div>

            {/* Category Description */}
            <AnimatePresence mode="wait">
              {hoveredCategory && (
                <motion.div
                  key={hoveredCategory}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="text-center mt-4"
                >
                  <p className="text-muted lead mb-0">
                    {categories.find(c => c.key === hoveredCategory)?.content || 
                     categories.find(c => c.key === hoveredCategory)?.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Projects Grid */}
        {loading ? (
          <LoadingSpinner text="Loading impactful projects..." />
        ) : (
          <motion.div 
            className="row g-4"
            layout
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <AnimatePresence mode="wait">
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    className="col-12 col-md-6 col-lg-4"
                    variants={itemVariants}
                    layout
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProjectCard 
                      project={project} 
                      featured={featuredProjects.some(fp => fp.id === project.id)}
                    />
                  </motion.div>
                ))
              ) : (
                <motion.div 
                  className="col-12 text-center py-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-muted">
                    <div className="fs-1 mb-3">📂</div>
                    <h4 className="fw-semibold text-dark mb-2">No Projects Found</h4>
                    <p className="text-muted">We're working on new initiatives in this category.</p>
                    <button 
                      className="btn btn-outline-success mt-2"
                      onClick={() => setActiveCategory('all')}
                    >
                      View All Projects
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Impact Stats */}
        <motion.div 
          className="row mt-5 pt-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="col-12">
            <div className="bg-success bg-opacity-10 rounded-4 p-5">
              <div className="row g-4 text-center">
                <motion.div 
                  className="col-6 col-md-3"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-success fw-black display-6 mb-2">50K+</div>
                  <div className="text-dark fw-semibold">Lives Impacted</div>
                </motion.div>
                <motion.div 
                  className="col-6 col-md-3"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-success fw-black display-6 mb-2">100+</div>
                  <div className="text-dark fw-semibold">Communities Reached</div>
                </motion.div>
                <motion.div 
                  className="col-6 col-md-3"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-success fw-black display-6 mb-2">25K+</div>
                  <div className="text-dark fw-semibold">Trees Planted</div>
                </motion.div>
                <motion.div 
                  className="col-6 col-md-3"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-success fw-black display-6 mb-2">5K+</div>
                  <div className="text-dark fw-semibold">Youth Empowered</div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="row mt-5 text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="col-12">
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="/programs" 
                className="btn btn-success btn-lg px-5 py-3 fw-bold shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)',
                  border: 'none',
                  borderRadius: '12px'
                }}
              >
                Explore All Programs  →
              </a>
            </motion.div>
            
            <motion.p 
              variants={itemVariants}
              className="text-muted mt-3"
            >
              Join us in creating lasting impact across African communities
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;